import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { IArticle, IArticleAndAttachments } from "../models/article.ts";
import { transformFromArticleToCSV } from "../lib/transform/transformFromArticleToCsv.ts";
import { formatBodyForCsv } from "../lib/formatBodyForCsv.ts";
import { createCSVFile, downloadZip } from "../lib/exportCsv.ts";
import { CsvEncoding } from "../lib/config.ts";
import { StreamingZipBuilder } from "../lib/createZipFile.ts";
import { proxyHttpRequest } from "../lib/api/proxyHttpRequest.ts";
import { IArticleAttachment } from "../models/articleAttachment.ts";
import { getCurrentDateTime } from "../lib/getCurrentDateTime.ts";
import { CancelError, CancelManager } from "../lib/cancelManager.ts";
import { ExportProgressStatus } from "../modules/parts/ExportProgressModal.tsx";
import { ISection } from "../models/section.ts";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { extractInlineImageInfo } from "../lib/extractInlineImages.ts";

/**
 * Zendesk の記事と添付ファイルをエクスポートし、ZIP ファイルとしてダウンロードする関数
 *
 * 1. Zendesk API を使用して記事の一覧を取得
 * 2. 記事に関連するカテゴリ情報を取得
 * 3. 記事ごとの添付ファイル情報を取得
 * 4. 記事本文中のインライン画像を抽出・ダウンロード
 * 5. 添付ファイルのデータをダウンロード
 * 6. CSV ファイルを作成し、添付ファイルとともに ZIP 圧縮
 *
 * @param {string} brandUrl - Zendesk のブランド URL
 * @param {string} token - 認証トークン
 * @param {(count: number) => void} onUpdateTotalCount - 記事の総数を更新するコールバック
 * @param {(count: number) => void} onUpdateDownloadedCount - ダウンロード済み記事数を更新するコールバック
 * @param {(status: ExportProgressStatus, message: string) => void} onUpdateInfo - 進捗情報を更新するコールバック
 */
interface ExportArticlesParams {
  brandUrl: string;
  token: string;
  createdStartDate?: string;
  createdEndDate?: string;
  updatedStartDate?: string;
  updatedEndDate?: string;
  titleKeyword?: string;
  categoryIds?: number[];
  sectionIds?: number[];
  onUpdateTotalCount: (count: number) => void;
  onUpdateDownloadedCount: (count: number) => void;
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void;
  csvEncoding: CsvEncoding;
}

export const exportArticlesAndAttachments = async (params: ExportArticlesParams) => {
  const {
    brandUrl,
    token,
    createdStartDate,
    createdEndDate,
    updatedStartDate,
    updatedEndDate,
    titleKeyword,
    categoryIds,
    sectionIds,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    onUpdateInfo,
    csvEncoding,
  } = params;

  const cancelManager = CancelManager.getInstance();
  cancelManager.reset();

  try {
    onUpdateInfo("Export", "記事をダウンロードしています。(1/6)");

    // List Articles APIで全記事を取得し、クライアント側でフィルタリングを行う
    // （Search APIはlocale・category等の組み合わせで正常動作しないケースがあるため使用しない）
    let articles = await getPaginatedData<IArticle>(
      "help_center/ja/articles.json",
      token,
      brandUrl,
      "articles",
    );

    // 日付フィルタリング
    if (createdStartDate) {
      articles = articles.filter((a) => a.created_at.slice(0, 10) >= createdStartDate);
    }
    if (createdEndDate) {
      articles = articles.filter((a) => a.created_at.slice(0, 10) <= createdEndDate);
    }
    if (updatedStartDate) {
      articles = articles.filter((a) => a.updated_at.slice(0, 10) >= updatedStartDate);
    }
    if (updatedEndDate) {
      articles = articles.filter((a) => a.updated_at.slice(0, 10) <= updatedEndDate);
    }

    // タイトルキーワードフィルタリング
    if (titleKeyword) {
      const kw = titleKeyword.toLowerCase();
      articles = articles.filter((a) => a.title?.toLowerCase().includes(kw));
    }

    if (articles.length === 0) {
      onUpdateInfo("Success", "エクスポート対象の記事はありませんでした。");
      return;
    }

    onUpdateInfo("Export", "記事のカテゴリ情報をダウンロードしています。(2/6)");
    const articlesWithCategories = await getArticlesWithCategories(
      token,
      brandUrl,
      articles,
    );

    // カテゴリーフィルタリング（セクション→カテゴリーのマッピング後に適用）
    let filteredArticles = articlesWithCategories;
    if (categoryIds && categoryIds.length > 0) {
      filteredArticles = articlesWithCategories.filter((a) =>
        categoryIds.includes(a.category_id),
      );
    }
    if (sectionIds && sectionIds.length > 0) {
      filteredArticles = filteredArticles.filter((a) =>
        sectionIds.includes(a.section_id),
      );
    }
    if (filteredArticles.length === 0) {
      onUpdateInfo("Success", "エクスポート対象の記事はありませんでした。");
      return;
    }

    onUpdateInfo(
      "Export",
      "記事の添付ファイル情報をダウンロードしています。(3/6)",
    );
    const articleAndAttachments = await getArticleAndAttachments(
      filteredArticles,
      token,
      brandUrl,
      onUpdateTotalCount,
      onUpdateDownloadedCount,
    );

    // ストリーミングZIPビルダーを作成（ファイルを逐次追加しメモリを節約）
    const zipBuilder = new StreamingZipBuilder();

    onUpdateInfo(
      "Export",
      "記事のインライン画像を処理しています。(4/6)",
    );
    const modifiedArticleAndAttachments =
      await extractAndDownloadInlineImages(
        articleAndAttachments,
        token,
        zipBuilder,
        onUpdateTotalCount,
        onUpdateDownloadedCount,
      );

    onUpdateInfo("Export", "記事の添付ファイルをダウンロードしています。(5/6)");
    await downloadAttachmentsToZip(
      modifiedArticleAndAttachments,
      token,
      zipBuilder,
      onUpdateTotalCount,
      onUpdateDownloadedCount,
    );

    cancelManager.checkCancelled();

    onUpdateInfo("Export", "Zipファイルを作成しています。(6/6)");

    const csv = modifiedArticleAndAttachments.map((articleAndAttachment) =>
      transformFromArticleToCSV(articleAndAttachment, formatBodyForCsv),
    );
    const csvFile = createCSVFile(csv, undefined, csvEncoding);
    zipBuilder.addFile("import.csv", csvFile);
    const zipFile = zipBuilder.finish();

    onUpdateInfo("Success", "");
    downloadZip(zipFile, `articles-${getCurrentDateTime()}`);
  } catch (error) {
    if (error instanceof CancelError) {
      onUpdateInfo("Cancel", error.message);
    } else {
      onUpdateInfo("Cancel", `ファイルの取得に失敗しました。: ${error}`);
    }
  }
};

/**
 * 取得した記事にカテゴリ情報を追加する関数
 *
 * 記事データにはカテゴリ情報が付いていないため、記事の `section_id` をもとに、対応する `category_id` を追加する。
 *
 * @param {string} token - 認証トークン
 * @param {string} brandUrl - Zendesk のブランド URL
 * @param {IArticle[]} articles - 記事の一覧
 * @returns {Promise<IArticle[]>} - カテゴリ情報を追加した記事の一覧
 */
const getArticlesWithCategories = async (
  token: string,
  brandUrl: string,
  articles: IArticle[],
): Promise<IArticle[]> => {
  const sections = await getPaginatedData<ISection>(
    "help_center/sections.json",
    token,
    brandUrl,
    "sections",
  );

  return articles.map((article) => {
    const section = sections.find(
      (section) => article.section_id === section.id,
    ) as ISection;
    return {
      ...article,
      category_id: section?.category_id,
    };
  });
};

/**
 * 記事ごとの添付ファイル情報を取得する関数
 *
 * @param {IArticle[]} articles - 記事の一覧
 * @param {string} token - 認証トークン
 * @param {string} brandUrl - Zendesk のブランド URL
 * @param {(count: number) => void} onUpdateDownloadedCount - ダウンロード済み記事数を更新するコールバック
 * @param {(status: ExportProgressStatus, message: string) => void} onUpdateInfo - 進捗情報を更新するコールバック
 * @returns {Promise<IArticleAndAttachments[]>} - 記事と添付ファイル情報のリスト
 */
/** 添付ファイル情報のAPI並列取得の同時実行数 */
const ATTACHMENT_FETCH_CONCURRENCY = 3;

const getArticleAndAttachments = async (
  articles: IArticle[],
  token: string,
  brandUrl: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
) => {
  const cancelManager = CancelManager.getInstance();
  onUpdateTotalCount(articles.length);

  // 結果を記事の順序通りに格納する配列
  const results: IArticleAndAttachments[] = new Array(articles.length);
  let completedCount = 0;

  // 同時実行数を制限した並列処理
  const fetchAttachment = async (index: number) => {
    cancelManager.checkCancelled();

    const article = articles[index];
    const { article_attachments } = await zendeskHttpRequest<{
      article_attachments: IArticleAttachment[];
    }>(
      `help_center/articles/${article.id}/attachments`,
      token,
      "GET",
      brandUrl,
    );

    results[index] = {
      article: article,
      attachments: article_attachments,
    };
    completedCount++;
    onUpdateDownloadedCount(completedCount);
  };

  // 同時実行数制御のワーカー
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < articles.length) {
      cancelManager.checkCancelled();
      const idx = nextIndex++;
      await fetchAttachment(idx);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const workers = Array.from(
    { length: Math.min(ATTACHMENT_FETCH_CONCURRENCY, articles.length) },
    () => worker(),
  );
  await Promise.all(workers);

  return results;
};

/**
 * 記事本文中のインライン画像を抽出・ダウンロードし、本文をプレースホルダーに置換する関数
 *
 * @param {IArticleAndAttachments[]} articleAndAttachments - 記事と添付ファイル情報のリスト
 * @param {string} token - 認証トークン
 * @param {(count: number) => void} onUpdateTotalCount - 画像の総数を更新するコールバック
 * @param {(count: number) => void} onUpdateDownloadedCount - ダウンロード済み画像数を更新するコールバック
 * @returns 置換済み記事リストとインライン画像ファイルリスト
 */
const extractAndDownloadInlineImages = async (
  articleAndAttachments: IArticleAndAttachments[],
  token: string,
  zipBuilder: StreamingZipBuilder,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
): Promise<IArticleAndAttachments[]> => {
  const cancelManager = CancelManager.getInstance();

  // 全記事から画像URLを抽出
  const extractionResults = articleAndAttachments.map((aa) =>
    extractInlineImageInfo(aa.article.id, aa.article.body),
  );

  const allImages = extractionResults.flatMap((result) => result.images);
  onUpdateTotalCount(allImages.length);

  // 画像をダウンロードし、即座にZIPに追加（メモリに蓄積しない）
  let downloadedCount = 0;

  const downloadImage = async (imageInfo: { originalUrl: string; filePath: string }) => {
    cancelManager.checkCancelled();

    const response = await proxyHttpRequest<ArrayBuffer>(
      token,
      imageInfo.originalUrl,
      true,
    );

    zipBuilder.addFile(imageInfo.filePath, new Uint8Array(response));
    downloadedCount++;
    onUpdateDownloadedCount(downloadedCount);
  };

  let nextImageIndex = 0;
  const imageWorker = async () => {
    while (nextImageIndex < allImages.length) {
      cancelManager.checkCancelled();
      const idx = nextImageIndex++;
      await downloadImage(allImages[idx]);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const imageWorkers = Array.from(
    { length: Math.min(ATTACHMENT_FETCH_CONCURRENCY, allImages.length) },
    () => imageWorker(),
  );
  await Promise.all(imageWorkers);

  // 置換済みbodyで記事リストを再構築
  return articleAndAttachments.map((aa, i) => ({
    ...aa,
    article: {
      ...aa.article,
      body: extractionResults[i].modifiedBody,
    },
  }));
};

/**
 * 添付ファイルのデータをダウンロードする関数
 *
 * @param {IArticleAndAttachments[]} articleAndAttachments - 記事と添付ファイル情報のリスト
 * @param {string} token - 認証トークン
 * @returns {Promise<IAttachmentFile[]>} - ダウンロードした添付ファイルのリスト
 */
const downloadAttachmentsToZip = async (
  articleAndAttachments: IArticleAndAttachments[],
  token: string,
  zipBuilder: StreamingZipBuilder,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
) => {
  const cancelManager = CancelManager.getInstance();

  // 全添付ファイルをフラット配列にして並列ダウンロード
  const allAttachments = articleAndAttachments.flatMap((aa) =>
    aa.attachments.map((attachment) => attachment),
  );

  onUpdateTotalCount(allAttachments.length);
  let downloadedCount = 0;

  const downloadAttachment = async (attachment: IArticleAttachment) => {
    cancelManager.checkCancelled();

    const response = await proxyHttpRequest<ArrayBuffer>(
      token,
      attachment.content_url,
      true,
    );

    zipBuilder.addFile(
      `attachments/${attachment.display_file_name}`,
      new Uint8Array(response),
    );
    downloadedCount++;
    onUpdateDownloadedCount(downloadedCount);
  };

  let nextAttachmentIndex = 0;
  const attachmentWorker = async () => {
    while (nextAttachmentIndex < allAttachments.length) {
      cancelManager.checkCancelled();
      const idx = nextAttachmentIndex++;
      await downloadAttachment(allAttachments[idx]);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const attachmentWorkers = Array.from(
    { length: Math.min(ATTACHMENT_FETCH_CONCURRENCY, allAttachments.length) },
    () => attachmentWorker(),
  );
  await Promise.all(attachmentWorkers);
};
